import React, { useCallback, useRef, useState } from 'react';

import deleteIcon from '../../assets/images/my-meetings/delete.svg';
import editIcon from '../../assets/images/my-meetings/edit.svg';
import playIcon from '../../assets/images/my-meetings/play.svg';
import viewedIcon from '../../assets/images/my-library/viewed.svg';
import Checkbox from '../../components/Checkbox';
import DotsLoader from '../../components/DotsLoader';
import FolderTreeSelector from '../../components/FolderTreeSelector';
import classes from './styles.module.scss';

export default function ImportFileRow({
  row,
  isImporting,
  onRemove,
  onUpdate,
  onOpenFolderPicker,
  isFolderPickerOpen,
  selectedFolderId,
  onFolderSelect,
  formatFileSize,
}) {
  const [editTitle, setEditTitle] = useState(row.title);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const prevTitleRef = useRef(row.title);
  const inputRef = useRef(null);

  const isEditable = !isImporting && row.status === 'ready';

  const handleStartEditing = useCallback(() => {
    if (!isEditable) return;
    setIsTitleEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isEditable]);

  const handleTitleBlur = useCallback(() => {
    const trimmed = editTitle.trim();
    if (trimmed) {
      onUpdate(row.rowId, { title: trimmed });
      prevTitleRef.current = trimmed;
    } else {
      setEditTitle(prevTitleRef.current);
    }
    setIsTitleEditing(false);
  }, [editTitle, onUpdate, row.rowId]);

  const handleTitleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.target.blur();
      }
    },
    []
  );

  const handlePublishToggle = useCallback(() => {
    onUpdate(row.rowId, { publishNow: !row.publishNow });
  }, [onUpdate, row.rowId, row.publishNow]);

  const statusDisplay = () => {
    if (row.validationError) {
      return (
        <span className={`${classes.statusBadge} ${classes.statusError}`}>
          Error
        </span>
      );
    }
    switch (row.status) {
      case 'ready':
        return (
          <span className={`${classes.statusBadge} ${classes.statusReady}`}>
            Ready
          </span>
        );
      case 'creating':
        return (
          <span className={classes.statusInline}>
            Creating <DotsLoader pink />
          </span>
        );
      case 'uploading':
        return (
          <span className={classes.statusInline}>
            {row.progress > 0 ? `Uploading ${row.progress}%` : 'Uploading'}{' '}
            <DotsLoader pink />
          </span>
        );
      case 'attaching':
        return (
          <span className={classes.statusInline}>
            Attaching <DotsLoader pink />
          </span>
        );
      case 'processing': {
        const stepLabels = {
          preparing: 'Preparing',
          converting: 'Converting',
          merging: 'Merging',
          transcribing: 'Transcribing',
          finishing: 'Finishing',
        };
        const label = (row.processingStep && stepLabels[row.processingStep]) || 'Processing';
        return (
          <span className={classes.statusInline}>
            {label} <DotsLoader pink />
          </span>
        );
      }
      case 'done':
        return (
          <a
            href={`/my-videos/${row.meetingId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.statusPublished}
          >
            <img src={playIcon} alt="Play" className={classes.publishedPlayIcon} />
            <img src={viewedIcon} alt="Published" className={classes.publishedCheckIcon} />
          </a>
        );
      case 'draft':
        return (
          <span className={`${classes.statusBadge} ${classes.statusDraft}`}>
            Draft
          </span>
        );
      case 'failed':
        return (
          <span className={`${classes.statusBadge} ${classes.statusError}`}>
            {row.errorMessage || 'Failed'}
          </span>
        );
      default:
        return (
          <span className={`${classes.statusBadge} ${classes.statusReady}`}>
            {row.status}
          </span>
        );
    }
  };

  return (
    <tr>
      {!isImporting && (
        <td className={classes.removeCell}>
          <button
            type="button"
            className={classes.removeButton}
            onClick={() => onRemove(row.rowId)}
            title="Remove"
          >
            <img src={deleteIcon} alt="Remove" />
          </button>
        </td>
      )}
      <td>
        <div className={classes.titleCell}>
          {isTitleEditing ? (
            <input
              ref={inputRef}
              type="text"
              className={classes.titleInput}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
            />
          ) : (
            <>
              <span className={classes.titleDisplay} onClick={handleStartEditing}>
                {editTitle}
              </span>
              {isEditable && (
                <button type="button" className={classes.editButton} onClick={handleStartEditing}>
                  <img src={editIcon} alt="Edit" />
                </button>
              )}
            </>
          )}
        </div>
        {row.validationError && (
          <div className={classes.validationError}>{row.validationError}</div>
        )}
        {row.validationWarning && !row.validationError && (
          <div className={classes.validationWarning}>
            {row.validationWarning}
          </div>
        )}
      </td>
      <td>
        <span className={classes.fileSize}>
          {formatFileSize(row.file.size)}
        </span>
      </td>
      <td>
        <div
          className={`${classes.folderCell} ${
            isFolderPickerOpen ? classes.folderCellExpanded : ''
          }`}
        >
          {row.folderName ? (
            <button
              type="button"
              className={classes.folderName}
              title={row.folderName}
              onClick={() => isEditable && onOpenFolderPicker(row.rowId)}
              disabled={!isEditable}
            >
              {row.folderName}
            </button>
          ) : (
            <button
              type="button"
              className={classes.folderPlaceholder}
              onClick={() => onOpenFolderPicker(row.rowId)}
              disabled={!isEditable}
            >
              Select folder
            </button>
          )}
          {isFolderPickerOpen && isEditable && (
            <div className={classes.folderTreeInline}>
              <FolderTreeSelector
                showCheckboxes={false}
                showMeetings={false}
                showRoot={false}
                selectedFolderId={selectedFolderId}
                onFolderSelect={onFolderSelect}
                variant="compact"
              />
            </div>
          )}
        </div>
      </td>
      <td className={classes.publishCell}>
        <Checkbox
          isChecked={row.publishNow}
          onClick={handlePublishToggle}
          isDisabled={isImporting}
        />
      </td>
      <td className={classes.statusCell}>{statusDisplay()}</td>
    </tr>
  );
}
